import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import type { CannedResponse } from '../lib/types';
import { Icon } from './ui';

let cache: Promise<CannedResponse[]> | null = null;
/** The organization's canned responses, loaded once per page (admins' changes show after a reload). */
export function loadCanned(fresh = false): Promise<CannedResponse[]> {
  if (!cache || fresh) cache = api<CannedResponse[]>('GET', '/canned-responses').catch(() => []);
  return cache;
}

export function useCanned(channel: 'chat' | 'email'): CannedResponse[] {
  const [list, setList] = useState<CannedResponse[]>([]);
  useEffect(() => {
    void loadCanned().then((l) => setList(l.filter((c) => c.channel === 'all' || c.channel === channel)));
  }, [channel]);
  return list;
}

/** A canned response with its placeholders filled in for this customer and agent. */
export function fill(body: string, customer: string | null): string {
  const agent = useNova.getState().user?.name ?? '';
  const first = (s: string) => s.trim().split(/\s+/)[0] ?? '';
  return body
    .replace(/\{customer_first_name\}/g, first(customer ?? '') || 'there')
    .replace(/\{customer_name\}/g, customer?.trim() || 'there')
    .replace(/\{agent_first_name\}/g, first(agent))
    .replace(/\{agent_name\}/g, agent);
}

/** A button that opens the canned responses, searchable; picking one hands its text back. */
export function CannedPicker({
  channel,
  customer,
  onPick,
  align = 'left',
}: {
  channel: 'chat' | 'email';
  customer: string | null;
  onPick: (text: string) => void;
  /** Which side the list opens to: right at the right edge of a panel */
  align?: 'left' | 'right';
}) {
  const list = useCanned(channel);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const needle = q.trim().toLowerCase();
  const shown = list.filter((c) => !needle || c.title.toLowerCase().includes(needle) || c.body.toLowerCase().includes(needle) || (c.shortcut ?? '').includes(needle));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Canned responses"
        title="Canned responses"
        className="grid h-9 w-9 place-items-center rounded-lg border border-line text-ink-2 hover:border-line-strong hover:text-ink"
      >
        <Icon name="bolt" size={16} />
      </button>
      {open ? (
        <div className={`absolute bottom-11 ${align === 'right' ? 'right-0' : 'left-0'} z-50 w-[min(360px,80vw)] rounded-xl border border-line-strong bg-hull p-2 shadow-2xl shadow-black/40`} role="dialog" aria-label="Pick a canned response">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search canned responses" aria-label="Search canned responses" className="mb-2 w-full rounded-lg border border-line bg-abyss/60 px-3 py-1.5 text-sm outline-none focus:border-ion" />
          <ul className="max-h-64 overflow-auto">
            {shown.length === 0 ? (
              <li className="px-2 py-3 text-sm text-ink-3">{list.length ? 'None match.' : 'No canned responses yet. Admins add them under Admin > Channels > Canned responses.'}</li>
            ) : (
              shown.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(fill(c.body, customer));
                      setOpen(false);
                      setQ('');
                    }}
                    className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-veil/[0.05]"
                  >
                    <span className="block text-sm font-medium">
                      {c.title}
                      {c.shortcut ? <span className="ml-1.5 text-[11px] font-normal text-ink-3">/{c.shortcut}</span> : null}
                    </span>
                    <span className="block truncate text-[12px] text-ink-3">{c.body}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
